import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
  type DocumentReference,
} from 'firebase/firestore'
import {
  effectiveCode,
  materialCodeFromId,
  parseMaterialsCsv,
} from '@/features/materials/material-csv'
import { CategoryIndex } from '@/lib/category-ref'
import { getDbOrThrow } from '@/lib/firebase'
import { mapCategory, mapMaterial, mapUnit } from '@/lib/mappers'
import type { Material, MaterialCategory, UnitOfMeasure } from '@/lib/types'

/** What the console may write on a material. Ownership and timestamps are stamped here, not by the UI. */
export type MaterialDraft = {
  name: string
  nameI18n: Record<string, string>
  code: string
  /** Copy of the category's current name. */
  category: string
  /** The link to the category (`materialCategories` id); blank = none / legacy free text. */
  categoryId: string
  unit: string
  unitId: string
  description: string
  descriptionI18n: Record<string, string>
  costType: Material['costType']
  isActive: boolean
  isPrivate: boolean
}

/**
 * Live catalog: TWO listeners merged by document id — own items and published items.
 *
 * This is not an optimisation, it is a requirement: the security rules allow a material to be read
 * when it is public OR owned by the caller, and Firestore only accepts a query it can prove is
 * allowed. One unfiltered `collection('materials')` query would be rejected outright. The Android
 * app does exactly the same.
 */
export function subscribeToMaterials(
  uid: string,
  onData: (items: Material[]) => void,
  onError: (error: Error) => void,
): () => void {
  const db = getDbOrThrow()
  const owned = new Map<string, Material>()
  const shared = new Map<string, Material>()

  const push = () => {
    const merged = new Map(owned)
    for (const [id, item] of shared) merged.set(id, item)
    onData([...merged.values()].sort((left, right) => left.name.localeCompare(right.name, 'ru')))
  }

  const collect = (target: Map<string, Material>) => (snapshot: {
    docs: Array<{ id: string; data: () => Record<string, unknown> }>
  }) => {
    target.clear()
    for (const item of snapshot.docs) {
      const material = mapMaterial(item.id, item.data())
      if (material) target.set(material.id, material)
    }
    push()
  }

  const unsubscribeOwned = onSnapshot(
    query(collection(db, 'materials'), where('ownerId', '==', uid)),
    collect(owned),
    onError,
  )
  const unsubscribeShared = onSnapshot(
    query(collection(db, 'materials'), where('isPrivate', '==', false)),
    collect(shared),
    onError,
  )

  return () => {
    unsubscribeOwned()
    unsubscribeShared()
  }
}

/** Categories are readable by any signed-in user and writable by admins only. */
export function subscribeToCategories(
  onData: (items: MaterialCategory[]) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    collection(getDbOrThrow(), 'materialCategories'),
    (snapshot) => {
      const items = snapshot.docs
        .map((item) => mapCategory(item.id, item.data()))
        .filter((item): item is MaterialCategory => item !== null)
        // Sorted in memory (orderIndex, then name) so no index is needed.
        .sort((left, right) =>
          left.orderIndex !== right.orderIndex
            ? left.orderIndex - right.orderIndex
            : left.name.localeCompare(right.name, 'ru'),
        )
      onData(items)
    },
    onError,
  )
}

export function subscribeToUnits(
  onData: (items: UnitOfMeasure[]) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    collection(getDbOrThrow(), 'units'),
    (snapshot) => {
      const items = snapshot.docs
        .map((item) => mapUnit(item.id, item.data()))
        .filter((item): item is UnitOfMeasure => item !== null)
        .sort((left, right) => left.name.localeCompare(right.name, 'ru'))
      onData(items)
    },
    onError,
  )
}

/**
 * Creates or updates a material with exactly the fields the Android app writes, so both clients
 * produce identical documents.
 *
 * `id === null` creates: `ownerId`, `createdByName` and `createdAt` are stamped once and never
 * rewritten afterwards (an update uses `merge` and leaves them alone, which is also what the rules
 * demand — `ownerId` must stay equal).
 */
export function saveMaterial(
  id: string | null,
  draft: MaterialDraft,
  actor: { uid: string; label: string },
): Promise<string> {
  const db = getDbOrThrow()
  const ref = id === null ? doc(collection(db, 'materials')) : doc(db, 'materials', id)
  const shared = {
    name: draft.name,
    nameI18n: draft.nameI18n,
    code: draft.code,
    category: draft.category,
    categoryId: draft.categoryId,
    unit: draft.unit,
    unitId: draft.unitId,
    description: draft.description,
    descriptionI18n: draft.descriptionI18n,
    costType: draft.costType,
    isActive: draft.isActive,
    isPrivate: draft.isPrivate,
    updatedByName: actor.label,
    updatedAt: serverTimestamp(),
  }

  if (id === null) {
    return setDoc(ref, {
      ...shared,
      ownerId: actor.uid,
      createdByName: actor.label,
      createdAt: serverTimestamp(),
    }).then(() => ref.id)
  }
  return setDoc(ref, shared, { merge: true }).then(() => ref.id)
}

/** Allowed for the owner, and for an admin when the material is published. */
export function deleteMaterial(id: string): Promise<void> {
  return deleteDoc(doc(getDbOrThrow(), 'materials', id))
}

export type ImportResult = { imported: number; updated: number; skipped: number }

/**
 * Applies a CSV file to the catalog — a port of `ImportMaterialsUseCase`.
 *
 * Rows are keyed by the material's effective code, so exporting and re-importing pushes edits
 * instead of creating duplicates:
 *  - a matching code UPDATES in place (id, owner and creation time are kept);
 *  - a new or blank code INSERTS (a blank code is derived from the new id, so the row can be matched
 *    next time);
 *  - a blank name is skipped, and a code repeated inside one file is applied only once;
 *  - empty translation columns keep whatever the matched material already has (the file may predate
 *    those columns);
 *  - the category column holds a NAME; it is resolved to the catalog (`CategoryIndex`: exact, then
 *    ignoring case) and stored as `categoryId` + the current name. An unknown name stays free text
 *    with no id — unless it is the name the matched material already shows (its link is kept).
 *
 * Written in batches of 400 operations. A batch is atomic: if one row of a chunk is rejected by the
 * rules, none of that chunk is written — the same behaviour as the mobile import.
 */
export async function importMaterialsCsv(
  csvText: string,
  existing: Material[],
  categoryCatalog: MaterialCategory[],
  actor: { uid: string; label: string },
): Promise<ImportResult> {
  const db = getDbOrThrow()
  const rows = parseMaterialsCsv(csvText)
  const categories = new CategoryIndex(categoryCatalog)
  const byCode = new Map(existing.map((item) => [effectiveCode(item).toLowerCase(), item]))

  const writes: Array<{ ref: DocumentReference; data: Record<string, unknown>; merge: boolean }> = []
  const seen = new Set<string>()
  let imported = 0
  let updated = 0
  let skipped = 0

  for (const row of rows) {
    if (row.name.length === 0) {
      skipped++
      continue
    }
    const codeKey = row.code.toLowerCase()
    if (codeKey.length > 0 && seen.has(codeKey)) {
      skipped++
      continue
    }
    if (codeKey.length > 0) seen.add(codeKey)

    const match = codeKey.length > 0 ? byCode.get(codeKey) : undefined
    const category = categories.find('', row.category)
    const categoryName = category?.name ?? row.category
    if (match) {
      const keepLink =
        category === null &&
        row.category === categories.displayName(match.categoryId, match.category).trim()
      writes.push({
        ref: doc(db, 'materials', match.id),
        merge: true,
        data: {
          name: row.name,
          code: effectiveCode(match),
          category: categoryName,
          categoryId: category?.id ?? (keepLink ? match.categoryId : ''),
          unit: row.unit,
          description: row.description,
          isActive: row.isActive,
          isPrivate: row.isPrivate,
          nameI18n: Object.keys(row.nameI18n).length > 0 ? row.nameI18n : match.nameI18n,
          descriptionI18n:
            Object.keys(row.descriptionI18n).length > 0
              ? row.descriptionI18n
              : match.descriptionI18n,
          updatedByName: actor.label,
          updatedAt: serverTimestamp(),
        },
      })
      updated++
    } else {
      const ref = doc(collection(db, 'materials'))
      writes.push({
        ref,
        merge: false,
        data: {
          name: row.name,
          code: row.code.length > 0 ? row.code : materialCodeFromId(ref.id),
          category: categoryName,
          categoryId: category?.id ?? '',
          unit: row.unit,
          // The CSV carries no unit id; the symbol is what estimates display.
          unitId: '',
          description: row.description,
          nameI18n: row.nameI18n,
          descriptionI18n: row.descriptionI18n,
          costType: 'MATERIAL',
          isActive: row.isActive,
          isPrivate: row.isPrivate,
          ownerId: actor.uid,
          createdByName: actor.label,
          updatedByName: actor.label,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
      })
      imported++
    }
  }

  for (let start = 0; start < writes.length; start += BATCH_LIMIT) {
    const batch = writeBatch(db)
    for (const write of writes.slice(start, start + BATCH_LIMIT)) {
      if (write.merge) {
        batch.set(write.ref, write.data, { merge: true })
      } else {
        batch.set(write.ref, write.data)
      }
    }
    await batch.commit()
  }

  return { imported, updated, skipped }
}

const BATCH_LIMIT = 400
